import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionState } from '@microsoft/signalr';
import { useAuthStore } from '@/auth/auth-store';
import {
  ORG_NOTIFICATION_HUB_URL,
  createHubConnection,
} from '@/scheduling/realtime/signalr-connection';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';

/**
 * useMacroRealtime — wires the Macros tab to the
 * `/api/practice/OrganizationNotificationHub` SignalR hub.
 *
 * **What it subscribes to**
 *
 *   `MacroUpdated` on `/api/practice/OrganizationNotificationHub`
 *
 * Mirrors Flutter `macro_tab.dart:138-149` (`signalRCall(service:
 * SignalRService.Macro, methodName: 'MacroUpdated', onTrigger: ...)`).
 * Flutter resolves the hub URL via `signalRHub(SignalRService.Macro)`
 * in `scrips_core/constants/api_constants.dart:555-557` — the same
 * relative path used here.
 *
 * **What we do on event**
 *
 * Invalidate the provider-scoped macros cache. The hook is keyed
 * `['settings', 'macros-by-provider-id', providerId, ...]` and uses
 * a predicate-based invalidation so every cache slot for this
 * practitioner (i.e. every (orgId, search) combination cached) refetches
 * on the next observer. Mirrors Flutter's `fetchList()` callback which
 * re-fires `GetMacrosByProviderIdEvent` on every `MacroUpdated` ping.
 *
 * **What we DON'T do**
 *
 * The hub payload is intentionally not consumed — Flutter treats it as
 * a fire-and-refetch signal (the handler's `response` argument is
 * unused; only `fetchList()` is called). We match that contract: any
 * MacroUpdated emission triggers a full refetch of the current
 * practitioner's macro list. Keeps the wire-payload shape from
 * becoming a contract we'd have to maintain.
 *
 * **Lifecycle**
 *
 * Connection is created lazily once `providerId` is known, started
 * in an effect, and stopped on unmount or practitioner change. Same
 * StrictMode-safe `disposed` flag dance as `usePatientChartRealtime` /
 * `useBillingRealtime`.
 *
 * **Why the hub URL is org-scoped, not practitioner-scoped**
 *
 * Flutter listens to a single org-wide hub and filters client-side.
 * Any `MacroUpdated` ping triggers a refetch — even ones for other
 * practitioners' macros — but the GET that follows is providerId-
 * scoped, so the cache only gets re-warmed with the current
 * practitioner's macros. Effectively idempotent over-fetch in exchange
 * for a stateless hub contract.
 */
export function useMacroRealtime(
  providerId: string | null | undefined,
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!providerId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(ORG_NOTIFICATION_HUB_URL, getToken);

    hub.on('MacroUpdated', () => {
      // Invalidate every cached macros slot for this practitioner — the
      // search-term axis means multiple cache entries can exist (one
      // per search query). Same predicate prefix as the
      // `macrosByProviderIdQueryKey` factory in
      // `@/settings/services/use-macros-by-provider-id.ts` — if that
      // factory changes shape, update the prefix check here too.
      // Mirrors the post-save invalidation in `useSaveMacro` (PR-B).
      //
      // Dedup-aware: when the CURRENT user saved the macro, the
      // backend echoes `MacroUpdated` back here ~ms later — the
      // mutation's onSuccess invalidate already kicked off the
      // refetch, so skip this echoed invalidate if a fetch is already
      // in flight. Same pattern as the chart-tab write surfaces.
      invalidateIfNotInFlight(queryClient, {
        predicate: (q) => {
          const key = q.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'settings' &&
            key[1] === 'macros-by-provider-id' &&
            key[2] === providerId
          );
        },
      });
    });

    let disposed = false;
    const startSafely = async (): Promise<void> => {
      try {
        if (hub.state === HubConnectionState.Disconnected) {
          await hub.start();
        }
        if (disposed && hub.state !== HubConnectionState.Disconnected) {
          await hub.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode-induced
        // negotiation aborts are expected dev-only noise.
      }
    };

    void startSafely();

    return () => {
      disposed = true;
      if (hub.state === HubConnectionState.Connected) {
        void hub.stop().catch(() => {});
      }
    };
  }, [providerId, queryClient]);
}

